import React, { useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

import TopPage from 'components/Shared/TopPage';
import { primary } from 'helpers/colors';

import {
  IMPROVEMENTS,
  IMPROVEMENT_CATEGORY_CONFIG,
  getLastSeenImprovementId,
  markImprovementsAsSeen,
  dismissImprovementsFirstAccess,
  Improvement
} from './constants';

function formatReleaseDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
}

type TimelineItemProps = {
  improvement: Improvement;
  isNew: boolean;
  isLast: boolean;
};

function TimelineItem({ improvement, isNew, isLast }: TimelineItemProps) {
  const category = IMPROVEMENT_CATEGORY_CONFIG[improvement.category];

  return (
    <Box sx={{ display: 'flex', gap: 2, position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: 24
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: isNew ? category.border : '#fff',
            border: `3px solid ${category.border}`,
            mt: 0.5,
            zIndex: 1,
            boxShadow: isNew ? `0 0 0 4px ${category.background}` : 'none',
            transition: 'all 0.2s'
          }}
        />
        {!isLast && (
          <Box
            sx={{
              flexGrow: 1,
              width: 2,
              backgroundColor: '#e6e4f1',
              my: 0.5
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          pb: isLast ? 0 : 4,
          maxWidth: 720
        }}
      >
        <Box
          sx={{
            border: '1px solid',
            borderColor: isNew ? category.border : '#ececec',
            borderRadius: 3,
            backgroundColor: isNew ? '#fbfbff' : '#fff',
            p: { xs: 2, sm: 2.5 },
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: primary.light,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1
            }}
          >
            <Chip
              label={category.label}
              size='small'
              sx={{
                color: category.color,
                backgroundColor: category.background,
                border: `1px solid ${category.border}`,
                fontWeight: 600,
                fontSize: 12
              }}
            />
            {isNew && (
              <Chip
                label='Novo'
                size='small'
                sx={{
                  ml: 'auto',
                  color: primary.contrast,
                  backgroundColor: primary.main,
                  fontWeight: 700,
                  fontSize: 11,
                  height: 20
                }}
              />
            )}
          </Box>

          <Typography variant='h6' sx={{ fontWeight: 700, color: primary.dark, fontSize: 18 }}>
            {improvement.title}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, mb: 1 }}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 14, color: '#9e9e9e' }} />
            <Typography variant='caption' sx={{ color: '#9e9e9e' }}>
              {formatReleaseDate(improvement.releaseDate)}
            </Typography>
          </Box>

          <Typography variant='body2' sx={{ color: '#555', lineHeight: 1.6 }}>
            {improvement.description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ImprovementsPath() {
  const lastSeenId = useMemo(() => getLastSeenImprovementId(), []);

  const improvements = useMemo(() => [...IMPROVEMENTS].sort((a, b) => b.id - a.id), []);

  useEffect(() => {
    markImprovementsAsSeen();
    dismissImprovementsFirstAccess();
  }, []);

  return (
    <>
      <TopPage title='Path de Atualizações' />

      <Card sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            background: `linear-gradient(135deg, ${primary.main} 0%, ${primary.hover} 100%)`,
            color: primary.contrast
          }}
        >
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              O que há de novo na UP
            </Typography>
            <Typography variant='body2' sx={{ opacity: 0.9, mt: 0.5, maxWidth: 720 }}>
              Acompanhe abaixo, da mais recente para a mais antiga, todas as novidades, melhorias e correções que
              entregamos para tornar a sua experiência cada vez melhor.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 4
          }}
        >
          <Typography variant='caption' sx={{ color: '#9e9e9e', fontWeight: 600 }}>
            Legenda:
          </Typography>
          {Object.values(IMPROVEMENT_CATEGORY_CONFIG).map(category => (
            <Box key={category.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: category.border
                }}
              />
              <Typography variant='caption' sx={{ color: '#666' }}>
                {category.label}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box>
          {improvements.map((improvement, index) => (
            <TimelineItem
              key={improvement.id}
              improvement={improvement}
              isNew={improvement.id > lastSeenId}
              isLast={index === improvements.length - 1}
            />
          ))}
        </Box>
      </Card>
    </>
  );
}

export default ImprovementsPath;
